import React, { useState } from "react";
import moment from "moment-timezone";
import { Box, Group, Text, Badge, Menu, ActionIcon } from "@mantine/core";
import {
  IconDotsVertical,
  IconPlayerPlay,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import type { AppointmentScheduledMessage } from "../../types/appointmentScheduledMessages";
import {
  STATUS_COLORS,
  SKIPPED_REASON,
  CHANGED_REASON,
  PENDING_STATUSES,
  POST_APPOINTMENT_DEFINITION_KEY,
  getScheduledMessageDisplayName,
} from "../../utils/appointmentScheduledMessageDisplay";
import type { AppointmentScheduledMessageActions } from "../../hooks/useAppointmentScheduledMessageActions";
import type { PreviewState } from "../../hooks/useAppointmentScheduledMessagePreviews";
import { AppointmentMessagePreview } from "../common/AppointmentMessagePreview";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import styles from "./ScheduledPanel.module.css";

interface AppointmentScheduledRowProps {
  item: AppointmentScheduledMessage;
  timezone: string;
  /** Shared send-now / cancel / retry actions (from useAppointmentScheduledMessageActions). */
  actions: AppointmentScheduledMessageActions;
  /** Rendered SMS body for this row (from useAppointmentScheduledMessagePreviews). */
  preview?: PreviewState;
}

/**
 * Row for an AUTOMATED appointment scheduled message inside the two-way chat
 * "Scheduled messages" drawer (booking confirmations, reminders,
 * reschedule/cancel, forms/waivers, post-appointment). Shows the same label +
 * status the Transaction Modal uses and offers the same send-now / cancel /
 * retry on actionable rows — driven by the shared actions hook (identical
 * backend endpoints), keyed by the message's own transaction.
 */
export const AppointmentScheduledRow: React.FC<
  AppointmentScheduledRowProps
> = ({ item, timezone, actions, preview }) => {
  const [cancelOpen, setCancelOpen] = useState(false);
  const busy = actions.actionLoading === item.uuid;

  const when = moment
    .utc(item.scheduled_at)
    .tz(timezone)
    .format("MMM D, YYYY · h:mm A z");

  const isPending = PENDING_STATUSES.has(item.status);
  // Actions need the row's own transaction to hit the per-appointment endpoints.
  const canAct = Boolean(item.transaction_uuid);
  // Mirror the Transaction Modal: Send Now on pending non-form rows, Retry on
  // FAILED, Cancel on pending.
  const canSendNow =
    canAct && isPending && item.message_type !== "form_request";
  const canRetry = canAct && item.status === "FAILED";
  const canCancel = canAct && isPending;
  const hasActions = canSendNow || canRetry || canCancel;

  const reason =
    item.status === "FAILED" && item.failure_reason
      ? item.failure_reason
      : item.status === "SKIPPED"
        ? SKIPPED_REASON
        : item.status === "CHANGED"
          ? CHANGED_REASON
          : null;

  return (
    <Box className={styles.row}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <Box className={styles.rowText}>
          <Group gap="xs" wrap="wrap">
            <Text size="sm" fw={500}>
              {getScheduledMessageDisplayName(item)}
            </Text>
            <Badge
              size="xs"
              variant="light"
              color={STATUS_COLORS[item.status] ?? "gray"}
            >
              {item.status}
            </Badge>
            {item.definition_key === POST_APPOINTMENT_DEFINITION_KEY && (
              <Badge size="xs" variant="light" color="teal">
                Post-Appointment
              </Badge>
            )}
          </Group>
          <Box mt={6}>
            <AppointmentMessagePreview preview={preview} status={item.status} />
          </Box>
          <Text size="xs" c="dimmed" mt={4}>
            {item.status === "SENT"
              ? `Sent ${moment.utc(item.updated_at).tz(timezone).format("MMM D, YYYY · h:mm A z")}`
              : item.status === "CHANGED"
                ? `Rescheduled to ${when}`
                : `Scheduled for ${when}`}
          </Text>
          {reason && (
            <Text
              size="xs"
              c={item.status === "FAILED" ? "red" : "dimmed"}
              mt={2}
            >
              {reason}
            </Text>
          )}
        </Box>
        {hasActions && (
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                loading={busy}
                aria-label="Message actions"
              >
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {canSendNow && (
                <Menu.Item
                  leftSection={<IconPlayerPlay size={14} />}
                  onClick={() => actions.sendNow(item)}
                >
                  Send Now
                </Menu.Item>
              )}
              {canRetry && (
                <Menu.Item
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => actions.retry(item)}
                >
                  Retry
                </Menu.Item>
              )}
              {canCancel && (
                <Menu.Item
                  color="red"
                  leftSection={<IconX size={14} />}
                  onClick={() => setCancelOpen(true)}
                >
                  Cancel
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      <DeleteConfirmationModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={async () => {
          await actions.cancel(item);
          setCancelOpen(false);
        }}
        isLoading={busy}
        title="Cancel scheduled message"
        message={`Cancel "${getScheduledMessageDisplayName(item)}"? This cannot be undone.`}
        confirmLabel="Cancel send"
      />
    </Box>
  );
};

export default AppointmentScheduledRow;
